'use client';

import React, { useState, useTransition } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useStorefrontPath } from '@/components/storefront/StorefrontUrlContext';

interface StickySearchBarProps {
  tenantId: string;
  /** Placeholder text */
  placeholder?: string;
  /** Auto-focus on mount */
  autoFocus?: boolean;
  className?: string;
}

/**
 * Sticky inline search bar for the Immersive Commerce layout header.
 *
 * Blends into the header bar with minimal border-radius and no background.
 */
export default function StickySearchBar({
  tenantId,
  placeholder = 'Search products...',
  autoFocus = false,
  className = '',
}: StickySearchBarProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();
  const [searchValue, setSearchValue] = useState(searchParams.get('search') || '');
  const sp = useStorefrontPath(tenantId);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();

    const params = new URLSearchParams(searchParams);
    if (searchValue.trim()) {
      params.set('search', searchValue.trim());
      params.delete('page');
    } else {
      params.delete('search');
    }

    startTransition(() => {
      router.push(sp(`?${params.toString()}`));
    });
  };

  const handleClear = () => {
    setSearchValue('');
    const params = new URLSearchParams(searchParams);
    params.delete('search');
    params.delete('page');

    startTransition(() => {
      router.push(sp(`?${params.toString()}`));
    });
  };

  return (
    <form onSubmit={handleSearch} className={`relative w-full max-w-md ${className}`}>
      <div className="relative flex items-center">
        <svg
          className="absolute left-3 w-4 h-4 text-neutral-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>

        <input
          type="text"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder={placeholder}
          autoFocus={autoFocus}
          className="w-full pl-9 pr-16 py-2 bg-transparent border-b border-neutral-300 dark:border-neutral-600 text-neutral-900 dark:text-white placeholder-neutral-400 focus:outline-none focus:border-primary-500 transition-colors text-sm"
        />

        {searchValue && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-10 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
            aria-label="Clear search"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        <button
          type="submit"
          disabled={isPending}
          className="absolute right-0 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors disabled:opacity-50"
          aria-label="Search"
        >
          {isPending ? (
            <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          )}
        </button>
      </div>
    </form>
  );
}
